// Root layout (FR-012, FR-037, FR-040 to FR-042; research R3, R6-R9, R15). Holds the native splash
// until the fonts are ready, then: ThemeProvider → SessionProvider → CurrentSchoolProvider → RootShell
// (config error, or the loading screen while the session restores) → the routes, with the offline
// banner and the slow-connection banner above them.
import { Slot } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { StatusBar } from "expo-status-bar";
import { useEffect } from "react";

import { CurrentSchoolProvider } from "@/school-identity/CurrentSchoolProvider";
import { SessionProvider } from "@/session/SessionProvider";
import { EnvBadge } from "@/shell/EnvBadge";
import { NoticeHost } from "@/shell/notice/NoticeHost";
import { OfflineBanner } from "@/shell/OfflineBanner";
import { RootShell } from "@/shell/RootShell";
import { SlowNetworkBanner } from "@/shell/SlowNetworkBanner";
import { useAppFonts } from "@/theme/fonts";
import { ThemeProvider } from "@/theme/ThemeProvider";
import { useTheme } from "@/theme/useTheme";

void SplashScreen.preventAutoHideAsync().catch(() => {});

/**
 * Inside ThemeProvider, which renders nothing until the remembered theme has loaded: the splash
 * screen stays up until then (FR-022), and the status bar follows the resolved theme (FR-023).
 */
function ThemedFrame() {
  const { resolved } = useTheme();
  useEffect(() => {
    void SplashScreen.hideAsync().catch(() => {});
  }, []);
  return <StatusBar style={resolved === "dark" ? "light" : "dark"} />;
}

export default function RootLayout() {
  const fontsReady = useAppFonts();

  if (!fontsReady) return null;

  return (
    <ThemeProvider>
      <ThemedFrame />
      <EnvBadge />
      <OfflineBanner />
      <SlowNetworkBanner />
      <SessionProvider>
        <CurrentSchoolProvider>
          <RootShell>
            <Slot />
          </RootShell>
          <NoticeHost />
        </CurrentSchoolProvider>
      </SessionProvider>
    </ThemeProvider>
  );
}

// Renders outside the providers above, so the root fallback brings its own theme (FR-041).
export { RootErrorFallback as ErrorBoundary } from "@/shell/ErrorFallback";
